import { useEffect } from "react";
import Header from "components/Structure/Header/Header.js";
import InboxMainContainer from "./InboxMainContainer.js";
import InboxToolBar from "./Toolbar/InboxToolBar.js";
import DesktopSelectComponent from "./DesktopSelectComponent.js";
import SplitPanelComponent from "./SplitComponent/SplitInfoComponent.js";
import FullScreenMessage from "components/Reusable/FullScreenMessage/FullScreenMessage.js";
import FormsNoDataMessage from "./FormsNoDataMessage.tsx";
import useMediaQueries from "hooks/useMediaQueries.js";
import PickerIFrame from "../Picker/PickerIFrame.tsx";
import usePickerQueryParamActions from "../Picker/usePickerQueryParamActions.ts";
import PickerFullScreenMessage from "../../../../components/Reusable/FullScreenMessage/PickerFullScreenMessage.tsx";
import UploaderProvider from "contexts/uploaderContext/uploaderContext.js";
import ReuploadPortalWrapper from "components/Reusable/Reuploader/ReuploadPortalWrapper.tsx";
import useInbox from "hooks/useInbox.ts";
import { useInboxFetchLogic } from "./InboxFetchLogicComponent.js";
import { usePicker } from "contexts/pickerContext/pickerContext.ts";
import { useUser } from "contexts/userContext/userContext.js";
import { useAppSelector } from "store/hooks.ts";
import {
  selectSearchParams,
  selectTParam,
  selectPickerSearchParams,
  selectFParam,
} from "store/selectors/inboxSelectors.ts";

const TRUSTED_PICKER_ORIGIN = "picker-set.jumbomail.me";
const InboxContent = () => {
  const fullScreenMessageData = useInboxFetchLogic();
  const { setSplitView, getMailsData, setMailDataProp } = useInbox();
  const { desktopCondition } = useMediaQueries();
  const { initializeIframe, closeIframe } = usePickerQueryParamActions();
  const { getIsFetchingPickerData, fetchPickerMailList } = usePicker();
  const { getUserData, updateUserUsedStorage } = useUser();
  const searchParams = useAppSelector(selectSearchParams);
  const pickerSearchParams = useAppSelector(selectPickerSearchParams);
  const pageType = useAppSelector(selectTParam);
  const pageCategory = useAppSelector(selectFParam);

  const handlePickerRedirect = (event) => {
    //prettier-ignore
    if (
      event.data &&
      event.data.type === "go-back" &&
      event.origin.includes(TRUSTED_PICKER_ORIGIN)
    )
      window?.closeIframe() || closeIframe();
  };

  useEffect(() => {
    if (pageType !== "picker") return;

    window.addEventListener("message", handlePickerRedirect);

    return () => {
      window.removeEventListener("message", handlePickerRedirect);
    };
  }, [pageType]);

  useEffect(() => {
    // add global function to the Window in order to be able to close iframe within itself
    window.closeIframe = () => {
      // remove all query params except picker
      window.location.href =
        window.location.origin + window.location.pathname + `?t=picker`;
    };
  }, []);

  useEffect(() => {
    if (getUserData()?.UserEncryptedId) {
      fetchPickerMailList(getUserData().UserEncryptedId);
    }
  }, [getUserData()?.UserEncryptedId, pickerSearchParams?.p]);

  useEffect(() => {
    if (
      getUserData()?.UserEncryptedId &&
      (pageType === "picker" || searchParams?.q)
    ) {
      fetchPickerMailList(getUserData().UserEncryptedId);
    }
  }, [getUserData()?.UserEncryptedId, pageType, searchParams?.q]);

  useEffect(() => {
    getUserData()?.UserEncryptedId && initializeIframe();
  }, [getUserData()?.UserEncryptedId]);

  useEffect(() => {
    if (fullScreenMessageData) {
      setSplitView(false);
    }
  }, [fullScreenMessageData]);

  // handeling uploading of more files to certain mail via inbox
  const handleFinishUpload = ({
    newCrfToken,
    reuploadingMailId,
    uploadedFiles,
  }) => {
    const reuploadMailData = getMailsData()[reuploadingMailId];
    const newFilesCount =
      reuploadMailData.filesQuantity + uploadedFiles?.length;

    const uploadedSize = uploadedFiles.reduce((accumulator, currentObject) => {
      return accumulator + currentObject.size;
    }, 0);

    updateUserUsedStorage({
      UserUsedStorage: getUserData().UserUsedStorage + uploadedSize,
    });

    setMailDataProp(
      reuploadingMailId,
      ["CSRFtoken", "filesQuantity"],
      [newCrfToken || reuploadMailData.CSRFtoken, newFilesCount]
    );
  };

  return (
    <>
      <div className="inbox-content">
        {desktopCondition ? null : <Header />}
        <InboxToolBar />
        {fullScreenMessageData ? (
          pageType === "picker" &&
          !pickerSearchParams?.ps?.length &&
          !getIsFetchingPickerData() ? (
            searchParams?.q ? (
              <FullScreenMessage {...fullScreenMessageData} />
            ) : (
              <PickerFullScreenMessage />
            )
          ) : pageCategory === "jform" &&
            !searchParams?.sa?.length &&
            !searchParams?.sm?.length &&
            !searchParams?.sf?.length &&
            !searchParams?.q ? (
            <FormsNoDataMessage />
          ) : (
            <FullScreenMessage {...fullScreenMessageData} />
          )
        ) : (
          <div className="main-content">
            <div className="main-panel">
              <DesktopSelectComponent />
              <InboxMainContainer />
            </div>
            {desktopCondition && <SplitPanelComponent />}
          </div>
        )}
      </div>
      <UploaderProvider>
        <ReuploadPortalWrapper handleFinishUpload={handleFinishUpload} />
      </UploaderProvider>
      <PickerIFrame />
    </>
  );
};

export default InboxContent;
